// Lib
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Utils
import { getElementId } from '../../../../../../common/elements/utils/elementPropertyUtils';
import { getGridSize } from '../../../../../utils/grid/gridSizeSelector';
import getAttachment from '../../../../attachments/getAttachment';
import { getFetching } from '../../../../attachments/attachmentsSelector';
import { prop } from '../../../../../../common/utils/immutableHelper';

// Actions
import { navigateToElement } from '../../../../../reducers/navigationActions';
import { setSelectedElements } from '../../../../selection/selectionActions';

// Utils
import { hasAltModifier, hasCommandModifier } from '../../../../../utils/keyboard/keyboardUtility';

// Hooks
import useDocumentKeyEventHandler from '../../../../drawing/drawingEditor/drawingEditorCanvas/useDocumentKeyEventHandler';
import useStalledBoolean from '../../../../../utils/react/useStalledBoolean';
import { ImageModalV2Mode, useImageModalV2Mode } from './useImageModalV2Mode';
import { useImageModalV2Layout } from './useImageModalV2Layout';
import { useImageModalNavigation } from './useImageModalNavigation';
import { useImageModalV2BackgroundClose } from './useImageModalV2BackgroundClose';

// Constants
import { KEY_CODES } from '../../../../../utils/keyboard/keyConstants';
import { ELEMENT_ATTACHMENT_ACTION_TYPES } from '../../../../../../common/elements/elementConstants';

// Types
import { ImageModalV2EditModeProps, ImageModalV2ShellProps } from '../imageModalV2Types';
import { ReduxState } from '../../../../../types/reduxTypes';

const isRouteParamEnabled = (value?: string | boolean) => value === true || value === 'true';

const useImageModalV2TransferMode = ({
    hasAttachmentUpload,
    hasAttachmentDownload,
}: {
    hasAttachmentUpload: boolean;
    hasAttachmentDownload: boolean;
}) => {
    const lastTransferModeRef = useRef<ImageModalV2Mode.UPLOAD | ImageModalV2Mode.DOWNLOAD | null>(null);
    const hasActiveAttachmentTransfer = hasAttachmentUpload || hasAttachmentDownload;
    const showTransferMode = useStalledBoolean(hasActiveAttachmentTransfer, 0, 1000);

    if (hasAttachmentUpload) {
        lastTransferModeRef.current = ImageModalV2Mode.UPLOAD;
    } else if (hasAttachmentDownload) {
        lastTransferModeRef.current = ImageModalV2Mode.DOWNLOAD;
    }

    return showTransferMode ? lastTransferModeRef.current : null;
};

/**
 * Owns V2 image modal orchestration: route mode, layout measurement, navigation, and edit contracts.
 */
export const useImageModalShell = ({
    element,
    isEditable,
    location,
    close,
    closeCallbackRef,
}: ImageModalV2ShellProps) => {
    const dispatch = useDispatch();
    const attachment = useSelector((state: ReduxState) => getAttachment(state, { element }));
    const gridSize = useSelector(getGridSize);
    const attachmentActionType = prop('actionType', attachment);
    const isAttachmentTransferActive = !!getFetching(attachment);
    const hasAttachmentUpload =
        isAttachmentTransferActive && attachmentActionType === ELEMENT_ATTACHMENT_ACTION_TYPES.UPLOAD;
    const hasAttachmentDownload =
        isAttachmentTransferActive && attachmentActionType === ELEMENT_ATTACHMENT_ACTION_TYPES.DOWNLOAD;
    const transferMode = useImageModalV2TransferMode({ hasAttachmentUpload, hasAttachmentDownload });

    const modalRef = useRef<HTMLDivElement>(null);
    const primaryToolbarRef = useRef<HTMLDivElement>(null);
    const captionRef = useRef<HTMLDivElement>(null);

    const layout = useImageModalV2Layout({ element, attachment, gridSize, modalRef, primaryToolbarRef, captionRef });

    const elementId = getElementId(element);
    const isEditing = isRouteParamEnabled(location?.query?.editing);
    const isEditingDrawing = isRouteParamEnabled(location?.query?.['editing-drawing']);
    const returnToCanvasRef = useRef(isEditing || isEditingDrawing);

    const setRouteModeForElement = useCallback(
        (nextMode: ImageModalV2Mode) => {
            dispatch(
                navigateToElement({
                    elementId,
                    params: {
                        editing: nextMode === ImageModalV2Mode.CROP,
                        'editing-drawing': nextMode === ImageModalV2Mode.DRAW,
                    },
                }),
            );
        },
        [dispatch, elementId],
    );

    const { mode, setMode, isToolPanelOpen } = useImageModalV2Mode({
        isEditable,
        isEditing,
        isEditingDrawing,
        isUploadingAttachment: transferMode === ImageModalV2Mode.UPLOAD,
        isDownloadingAttachment: transferMode === ImageModalV2Mode.DOWNLOAD,
        setRouteMode: setRouteModeForElement,
    });

    const { canNavigate, orderedImageElements, navigateToPreviousImage, navigateToNextImage } = useImageModalNavigation(
        {
            element,
            enabled: mode === ImageModalV2Mode.VIEW,
        },
    );

    const finishEditingMode = useCallback(() => {
        if (returnToCanvasRef.current) {
            close();
            return;
        }

        setMode(ImageModalV2Mode.VIEW);
    }, [close, setMode]);

    const { handleModalMouseDown, handleModalClick } = useImageModalV2BackgroundClose({
        close,
        mode,
        finishEditingMode,
        closeCallbackRef,
    });

    const onKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (hasAltModifier(event) || hasCommandModifier(event) || !isEditable) return;

            switch (event.keyCode) {
                case KEY_CODES.P:
                case KEY_CODES.D:
                    setMode(ImageModalV2Mode.DRAW);
                    break;
                default:
                    break;
            }
        },
        [isEditable, setMode],
    );

    useDocumentKeyEventHandler({ onKeyDown, onKeyUp: undefined, onKeyPress: undefined });

    useEffect(() => {
        dispatch(setSelectedElements({ ids: [elementId], rangeAnchors: null, transactionId: undefined }));
    }, [dispatch, elementId]);

    useEffect(() => {
        (document.activeElement as HTMLElement)?.blur?.();
    }, [elementId, mode]);

    const editModeProps = useMemo<ImageModalV2EditModeProps>(
        () => ({
            element,
            gridSize,
            attachment,
            finishEditingMode,
            closeCallbackRef,
        }),
        [attachment, closeCallbackRef, element, finishEditingMode, gridSize],
    );

    return {
        modalRef,
        primaryToolbarRef,
        captionRef,
        mode,
        setMode,
        isToolPanelOpen,
        layout,
        editModeProps,
        canNavigate,
        orderedImageElements,
        navigateToPreviousImage,
        navigateToNextImage,
        handleModalMouseDown,
        handleModalClick,
    };
};
